import React from "react";

import CourseAPI from "./CoursesAPI";
import { Button } from "src/components/buttons";
import { Checkbox, Form, Input, Radio } from "antd";
import { Page, PageHero, Spinner } from "src/components/common";
import { Validation } from "src/common/utils";
import useGetDataHook from "src/admin/Organization/Courses/useGetDataHook";
import { Link } from "react-router-dom";
import AuthorizationWrapper from "src/common/AuthorizationWrapper";
import { AdminPermissions } from "src/common/Permissions";
import { toast } from "react-toastify";

interface IProps {
	history?: any;
	location?: any;
	match?: any;
	children?: any;
}

const initialCourse: CourseDTO = {
	id: 0,
	name: "",
	organizationId: 0,
	description: "",
	isActive: true,
	courseFile: "",
	standard: true,
};

const OrganizationCoursesEdit: React.FC = (props: IProps) => {
	const { organizationId, locationId, courseId } = props.match.params;

	const isEditPage: boolean = Number(courseId) > 0;

	const [submittingData, setSubmittingData] = React.useState<boolean>(false);

	const [{ data: course, isLoading: isLoading }, setCourse] =
		useGetDataHook<CourseDTO>(
			initialCourse,
			{
				apiCallFunc: CourseAPI.getOrganizationCourse,
				params: [{ organizationId, courseId }],
			},
			isEditPage
		);

	const validCourse = {
		name: Validation.validateStringIsNotEmpty(
			course.name!,
			"Please Enter a name"
		),
		description: Validation.validateStringIsNotEmpty(
			course.description!,
			"Please Enter a Description"
		),
		isActive: Validation.validateIsActive(course.isActive!), // cannot be undefined or null
	};

	return isLoading !== false ? (
		<Spinner size="full-page" />
	) : (
		<Page title="Course details">
			<PageHero>
				<Link
					to={`/admin/organizations/${organizationId}/locations/${locationId}/courses`}
				>
					&lt; COURSES
				</Link>
				<h1 className="mb-0">{isEditPage ? `${course.name}` : "New Course"}</h1>
			</PageHero>
			<div className="container-fluid">
				<div className="row">
					<div className="col-6 form-group">
						<label htmlFor="name">Course name</label>
						<Form.Item
							validateStatus={validCourse.name.validationStatus}
							help={validCourse.name.message}
							hasFeedback={true}
						>
							<AuthorizationWrapper
								type="disable"
								permissions={[AdminPermissions.EditOrganizationLocation]}
							>
								<Input
									name="name"
									value={course.name}
									onChange={onValueChange}
									required={true}
								/>
							</AuthorizationWrapper>
						</Form.Item>
					</div>
					<div className="col-3 form-group">
						<label htmlFor="Active">Active</label>
						<Form.Item
							validateStatus={validCourse.isActive.validationStatus}
							help={validCourse.isActive.message}
							hasFeedback={true}
						>
							<AuthorizationWrapper
								type="disable"
								permissions={[AdminPermissions.EditOrganizationLocation]}
							>
								<Radio.Group
									defaultValue="Active"
									onChange={onValueChange}
									name="isActive"
									value={course.isActive}
								>
									{[
										{ label: "Active", value: true },
										{ label: "InActive", value: false },
									].map((item) => {
										return (
											<Radio key={item.label} value={item.value}>
												{item.label}
											</Radio>
										);
									})}
								</Radio.Group>
							</AuthorizationWrapper>
						</Form.Item>
					</div>
				</div>
				<div className="row">
					<div className="col-6 form-group">
						<label htmlFor="description">Course description</label>
						<Form.Item
							validateStatus={validCourse.description.validationStatus}
							help={validCourse.description.message}
							hasFeedback={true}
						>
							<AuthorizationWrapper
								type="disable"
								permissions={[AdminPermissions.EditOrganizationLocation]}
							>
								<Input
									name="description"
									value={course.description}
									onChange={onValueChange}
									required={true}
								/>
							</AuthorizationWrapper>
						</Form.Item>
					</div>
					<div className="col-3 form-group">
						<label htmlFor="description">Standardized Course</label>
						<Form.Item hasFeedback={false}>
							<AuthorizationWrapper
								type="disable"
								permissions={[AdminPermissions.EditOrganizationLocation]}
							>
								<Checkbox
									id="standard"
									name="standard"
									defaultChecked
									checked={course.standard}
									value={course.standard}
									onChange={onValueCheck}
								>
									Standard
								</Checkbox>
							</AuthorizationWrapper>
						</Form.Item>
					</div>
				</div>
				<div className="row">
					<div className="col-3">
						<AuthorizationWrapper
							type="hide"
							permissions={[AdminPermissions.EditOrganizationLocation]}
						>
							<Button
								className="btn-primary"
								loading={submittingData}
								onClick={onSaveCourse}
								disabled={!isValidCourse(validCourse)}
							>
								Save
							</Button>
						</AuthorizationWrapper>
					</div>
				</div>
			</div>
		</Page>
	);

	function onValueCheck(e: any) {
		const { name, checked } = e.target;
		setCourse({ ...course, [name]: checked });
	}

	function onValueChange(e: any) {
		const { name, value } = e.target;
		setCourse({ ...course, [name]: value });
	}

	async function onSaveCourse() {
		setSubmittingData(true);
		const response = await CourseAPI.postOrganizationCourse({
			organizationId,
			id: courseId,
			name: course.name,
			description: course.description,
			isActive: course.isActive,
			courseFile: course.courseFile,
			standard: course.standard,
		});

		const res2: boolean = await CourseAPI.postLocationXrefCourse({
			organizationId,
			locationId,
			courseId: response.data.id,
		});
		setSubmittingData(false);
		if (response.status === 200 && res2) {
			toast.success(
				`Successfully ${isEditPage ? "updated" : "created"} course`
			);
			props.history.push(
				`/admin/organizations/${organizationId}/locations/${locationId}/courses`
			);
		} else {
			toast.error(`Error ${isEditPage ? "updating" : "creating"} course`);
		}
	}

	function isValidCourse(courseObjToBeValidated: any) {
		const validateValues: string[] = Object.entries(courseObjToBeValidated).map(
			([key, value]: [any, any]) => {
				return value.validationStatus;
			}
		);

		return Validation.isValid(validateValues);
	}
};

export default OrganizationCoursesEdit;
